import kebabCase from 'lodash/kebabCase';
import {formatUrl} from '@teemill/common/helpers';
import {Thumbnail} from '@teemill/common/classes';
import BlogTag from './blogTag';
import BlogPostSeries from './blogPostSeries';
import {Commentable} from '@teemill/modules/chat';

export default class Blog extends Commentable {
  constructor({
    id = null,
    title = null,
    description = null,
    metaImage = null,
    metaTitle = null,
    metaDescription = null,
    content = null,
    hidden = false,
    visibility = null,
    featured = false,
    published = false,
    showOnSitemap = true,
    keyPhraseCampaignId = undefined,
    division = null,
    tags = [],
    series = [],
    similarReads = [],

    publishedAt = null,
    updatedAt = null,

    url = null,
    editUrl = null,
    editListUrl = null,
    urlTitle = null,
    fullUrl = null,

    comments = [],

    commentable = [],
    social = [],
  } = {}) {
    super(comments);

    this.id = id;
    this.title = title;
    this.description = description;
    this.content = content;
    this.hidden = hidden;
    this.visibility = visibility;
    this.featured = featured;
    this.published = published;
    this.showOnSitemap = showOnSitemap;
    this.keyPhraseCampaignId = keyPhraseCampaignId;
    this.division = division;
    this.tags = BlogTag.map(tags);
    this.series = BlogPostSeries.map(series);
    this.similarReads = Blog.map(similarReads);

    this.publishedAt = publishedAt;
    this.updatedAt = updatedAt;

    this.url = url;
    this.editUrl = editUrl;
    this.editListUrl = editListUrl;
    this.urlTitle = urlTitle;
    this.fullUrl = fullUrl;

    this.image = new Thumbnail(metaImage);
    this.metaTitle = metaTitle;
    this.metaDescription = metaDescription;

    this.commentableType = 'blogPost';

    this.commentable = commentable;
    this.social = social;
  }

  /**
   * @name inSeries
   * @description Is this blog post part of a series
   *
   * @return {Boolean}
   */
  get inSeries() {
    return this.series.length > 0;
  }

  /**
   * @name firstTag
   * @description Get a blog post's first tag
   *
   * @return {BlogTag}
   */
  get firstTag() {
    return this.tags[0];
  }

  /**
   * Should the post appear in "list" views
   *
   * @returns {boolean}
   */
  get visible() {
    return !this.hidden;
  }

  /**
   * @name slugTitle
   * @description Get a url friendly slug from the post title
   *
   * @return {String}
   */
  get slugTitle() {
    return kebabCase(this.title);
  }

  /**
   * @name firstParagraph
   * @description Get the first paragraph of the post
   *
   * @return {String}
   */
  get firstParagraph() {
    return this.content
      ? this.content
          .replace(/<(?:.|\n)*?>/gm, '')
          .split('.')
          .slice(0, 3)
          .join('. ')
      : '';
  }

  /**
   * @name ensureSlugUrl
   * @description Fix the url to ensure it's compatible
   *
   * @return {Blog}
   */
  ensureSlugUrl() {
    if (this.urlTitle) {
      this.urlTitle = kebabCase(this.urlTitle);
    }

    return this;
  }

  /**
   * @name getFromDivision
   * @description Get a list of blogs that are linked to a division.
   *
   * @param {Division} division Division to get the blogs from.
   *
   * @return {Blogs[]} Blogs
   */
  static async getFromDivision(
    division,
    {liteResource = false, published = undefined, sitemap = undefined} = {}
  ) {
    const {data} = await $axios.get(formatUrl('/omnis/v3/blog/list/'), {
      params: {
        divisions: division.id,
        lite: liteResource,
        published,
        sitemap,
      },
    });

    return Blog.map(
      data.map(blogData => {
        blogData.image = {
          original: blogData.metaImage.original,
          1920: blogData.metaImage.banner,
          1140: blogData.metaImage.thumbnail,
        };

        return blogData;
      })
    );
  }

  /**
   * @name find
   * @description Find a specific blog post
   *
   * @param {string} urlTitle
   * @param {Division} division
   *
   * @return {Blog}
   */
  static find(urlTitle, division) {
    return $axios
      .get(formatUrl('/omnis/v3/blog/find/'), {
        params: {
          blogPost: urlTitle,
          divisions: [division],
        },
      })
      .success(blogPost => Blog.map(blogPost))
      .output();
  }

  /**
   * @name findById
   * @description Find a specific blog post by id
   *
   * @param {number} id
   * @param {Division} division
   *
   * @return {Blog}
   */
  static findById(id, division) {
    return $axios
      .get(formatUrl(`/omnis/v3/blog/${division}/posts/${id}`))
      .success(blogPost => Blog.map(blogPost))
      .output();
  }

  /**
   * @name create
   * @description Create an empty template of a blog post on the API
   *
   * @param {Int} division
   *
   * @return {Blog}
   */
  static create(division) {
    return $axios.post(formatUrl('/omnis/v3/blog/store/'), {
      division,
      published: false,
    });
  }

  /**
   * @name save
   * @description Sync changes to the post on the API
   *
   * @return {ApiResponse}
   */
  save() {
    return $axios
      .post(formatUrl('/omnis/v3/blog/update/'), {
        id: this.id,
        title: this.title,
        description: this.description,
        urlTitle: this.urlTitle,
        content: this.content,
        division: this.division.id,
        published: this.published,
        showOnSitemap: this.showOnSitemap,
        keyPhraseCampaignId: this.keyPhraseCampaignId,
        featured: this.featured,
        metaImage: this.image.original,
        metaTitle: this.metaTitle,
        metaDescription: this.metaDescription,
        hidden: this.hidden,
        tags: this.tags,
        visibility: this.visibility?.value,
      })
      .success(post => {
        Object.assign(this, post);
      }, false);
  }

  /**
   * @name uploadImage
   * @description Upload an image to this post
   *
   * @param {String} base64
   * @param {Boolean} metaImage
   *
   * @return {Object}
   */
  uploadImage({base64, metaImage}) {
    return $axios
      .post(formatUrl('/omnis/v3/blog/image-upload/'), {
        postId: this.id,
        base64,
        metaImage,
        division: this.division.id,
      })
      .success(({id, thumbnails}) => {
        if (metaImage) {
          this.image = Thumbnail.map(thumbnails);
        }

        return {id, thumbnails};
      })
      .output();
  }

  /**
   *
   * @param {BlogTag} tag
   */
  removeTag(tag) {
    this.tags = this.tags.filter(t => t.id !== BlogTag.map(tag).id);

    return this;
  }

  /**
   * @param {BlogTag} tag
   */
  addTag(tag) {
    this.tags.push(BlogTag.map(tag));

    return this;
  }

  /**
   * @name delete
   * @description Delete this post from the API
   *
   * @return {ApiResponse}
   */
  delete() {
    return $axios.post(formatUrl('/omnis/v3/blog/destroy/'), {
      division: this.division.id,
      postId: this.id,
    });
  }

  getEditUrl(context = 'teemill') {
    if (context === 'atlas') {
      return `/blogs/${this.division}/edit/${this.urlTitle}/`;
    }

    if (context === 'teemill') {
      return `/my-blogs/edit/${this.urlTitle}/`;
    }

    return undefined;
  }
}
